/* eslint-disable spire/export-styles */
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    className = "className",
    columns = "columns",
    gap = "gap",
    resizeRows = "resizeRows",
    collapseOnMobile = "collapseOnMobile",
    reverseOrderOnMobile = "reverseOrderOnMobile",
    backgroundColor ="backgroundColor",
}
interface Props extends WidgetProps {
    fields: {
        [fields.className]: string;
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.resizeRows]: boolean;
        [fields.collapseOnMobile]: boolean;
        [fields.reverseOrderOnMobile]: boolean;
        [fields.backgroundColor]: string;
    };
}

const Container = styled('div')<{gap: number, collapse: boolean, reverse: boolean, backgroundColor: string}>`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: ${p => `${p.gap  }px`};
    background-color: ${p => `${p.backgroundColor}`};
    ${({ theme, reverse }) => 
        (breakpointMediaQueries(theme, 
        [css`flex-direction: ${reverse ? 'column-reverse' : 'column'};`,
        css`flex-direction:  ${reverse ? 'column-reverse' : 'column'};`]))};
    ${p => {
        if (!p.collapse) {
            return css`
                flex-direction: row !important;
            `;
        }
    }}
    div[class^="GridWrapper"] {
        margin: 0 auto;
        div[class^="GridOffset"] {
            max-width: 100%;
            margin: 0;
            > div[class^="GridItemStyle"] {
                margin-bottom: 30px;
            }
        }
    }
`;

const RowItem = styled('div')<{width: number, resize: boolean}>`
    ${p => {
        if (!p.resize) {
            return css`
                width: ${`${p.width  }%`};
            `;
        } 
            return css`
                display: inline-flex;
                flex-basis: ${`${p.width  }%`};
                flex-grow: 1;
                flex-shrink: 1;
            `;
        
    }}
    ${({ theme }) => 
        (breakpointMediaQueries(theme, 
        [css`width: 100%;`,
        css`width:100%;`]))};

    > div {
        height: 100%;
        > div {
            max-height: 100%; 
        }
    }
`;

const FullWidthRow: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { className, columns, gap, resizeRows, collapseOnMobile, reverseOrderOnMobile, backgroundColor },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        columnElements.push(
            <RowItem key={x} width={(columns[x]/12)*100} resize={resizeRows}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </RowItem>,
        );
    }

    return <Container 
            className={className}
            collapse={collapseOnMobile === undefined ? true : collapseOnMobile} 
            gap={gap || 0} 
            reverse={reverseOrderOnMobile} 
            backgroundColor={backgroundColor || "#fff"}>
            {columnElements}
        </Container>;
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: FullWidthRow,
    definition: {
        group: "Basic",
        icon: "Row",
        fieldDefinitions: [
            {
                name: fields.className,
                displayName: "ClassName",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType:"Translatable",
            },
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.resizeRows,
                displayName: "Resize rows based on content",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General"
            },
            {
                name: fields.collapseOnMobile,
                displayName: "Collapse row on mobile",
                defaultValue: true,
                editorTemplate: "CheckboxField",
                fieldType: "General"
            },
            {
                name: fields.reverseOrderOnMobile,
                displayName: "Reverse order on mobile",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General"
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            }
        ],
    },
};

export default widgetModule;
